<template>
    <hh-form class="registration-form">
        <hh-form-group>
            <hh-email ref="model.Email"
                      class="registration-form__email registration-form__input"
                      placeholder="Enter your email address"
                      v-model="email"
                      @change="resetValidation"
                      @keyup.enter="register" />
        </hh-form-group>
        <hh-form-group>
            <hh-validator for="model.Email"
                          class="registration-form__validator registration-form__validator--email" />
        </hh-form-group>
        <hh-form-group>
            <hh-password def="password"
                         ref="model.Password"
                         class="registration-form__password registration-form__input --first-col"
                         placeholder="Enter a password"
                         v-model="password"
                         :auto-fill-disable="true"
                         @change="onPasswordChange"
                         @keyup.enter="register" />
            <hh-password def="confirm-password"
                         ref="model.ConfirmPassword"
                         class="registration-form__confirm-password registration-form__input --last-col"
                         placeholder="Confirm the password"
                         v-model="confirmPassword"
                         :auto-fill-disable="true"
                         @change="onConfirmPasswordChange"
                         @keyup.enter="register" />
        </hh-form-group>
        <hh-form-group>
            <hh-validator for="model.Password"
                          class="registration-form__validator registration-form__validator--password" />
        </hh-form-group>
        <hh-form-group>
            <hh-textbox def="given-name"
                        ref="model.FirstName"
                        class="registration-form__firstname registration-form__input --first-col"
                        placeholder="Enter your first name"
                        v-model="firstName"
                        :auto-fill="true"
                        @change="resetValidation" />
            <hh-textbox def="family-name"
                        ref="model.LastName"
                        class="registration-form__lastname registration-form__input --last-col"
                        placeholder="Enter your last name"
                        v-model="lastName"
                        :auto-fill="true"
                        @change="resetValidation" />
        </hh-form-group>
        <hh-form-group>
            <hh-validator for="model.FirstName"
                          class="registration-form__validator registration-form__validator--firstname --first-col" />
            <hh-validator for="model.LastName"
                          class="registration-form__validator registration-form__validator--lastname --last-col" />
        </hh-form-group>
        <hh-form-group>
            <hh-tel def="tel"
                    ref="model.ContactTelephoneNumber"
                    class="registration-form__tel registration-form__input"
                    placeholder="Enter your contact number"
                    v-model="contactTelephoneNumber"
                    :auto-fill="true"
                    @change="resetValidation" />
        </hh-form-group>
        <hh-form-group>
            <hh-validator for="model.ContactTelephoneNumber"
                          class="registration-form__validator registration-form__validator--tel" />
        </hh-form-group>
        <aside class="registration-form__data-notes">
            <p>
                This Plan Builder is provided by Parity Projects. We take our responsibility for your personal data seriously and you can find our privacy notice for the Plan Builder <a class="registration-form__link" tabindex="0" @click="onPrivacyClick">here</a>.
            </p>
            <p>
                When you register your information is shared with {{ schemeName }} and their partners so they can contact you to help you on your journey to a low carbon home.
                <span v-if="externalPolicy">You can find the {{ schemeName }} privacy notice <a class="registration-form__link" :href="externalPolicy" target="_blank">here</a>.</span>
            </p>
        </aside>
        <aside class="registration-form__data-notes">
            <p v-if="showMarketingConsent">
                {{ schemeName }} would love to send you news, offers and promotion — would you like to get these?
            </p>
            <hh-radio def="marketing"
                      v-if="showMarketingConsent"
                      ref="model.MarketingConsentGiven"
                      class="registration-form__consent-marketing registration-form__input registration-form__input--consent"
                      :options="marketingOptions"
                      v-model="marketingConsentGiven"
                      @change="resetValidation" />
            <hh-validator v-if="showMarketingConsent"
                          for="model.MarketingConsentGiven"
                          class="registration-form__validator registration-form__validator--consent-marketing" />
            <p v-if="showEvaluationConsent">
                Would you like {{ schemeName }} to share your data with an external research company (Winning Moves or CAG) who are
                evaluating the services? <i>The evaluators would only contact you for the evaluation and you can decide not to take
                    part in the evaluation when contacted.</i>
            </p>
            <hh-radio def="evaluation"
                      v-if="showEvaluationConsent"
                      ref="model.EvaluationConsentGiven"
                      class="registration-form__consent-evaluation registration-form__input registration-form__input--consent"
                      :options="evaluationOptions"
                      v-model="evaluationConsentGiven"
                      @change="resetValidation" />
            <hh-validator v-if="showEvaluationConsent"
                          for="model.EvaluationConsentGiven"
                          class="registration-form__validator registration-form__validator--consent-evaluation" />
        </aside>
        <aside class="registration-form__terms">
            <hh-label for-def="terms"
                      :tabstop="true">
                <hh-checkbox def="terms"
                             :tabstop="false"
                             ref="model.TermsAccepted"
                             class="registration-form__terms__box"
                             v-model="termsAccepted"
                             @change="resetValidation" />
                I agree to the site <a class="registration-form__terms__link" href="#" @click="onTermsClick">Terms &amp; Conditions</a>.
            </hh-label>
        </aside>
        <hh-form-group v-if="showRecaptcha">
            <hh-recaptcha class="registration-form__catch"
                          @verified="onReCaptchaVerify" />
        </hh-form-group>
        <hh-form-group>
            <hh-validator for="model.Recaptcha"
                          class="registration-form__validator registration-form__validator--catch" />
        </hh-form-group>
        <hh-error-display v-if="error"
                          class="registration-form__error"
                          :error="error" />
        <hh-btn type="cancel"
                class="registration-form__button registration-form__button--cancel"
                icon-class="fas fa-undo"
                icon-location="pre"
                text="Cancel"
                @click="$emit('canceled')" />
        <hh-btn type="cta"
                class="registration-form__button registration-form__button--create"
                icon-class="fas fa-user-plus"
                icon-location="pre"
                text="Create account"
                :track-operation-state="true"
                :operation-in-progress="registering"
                :disabled="isDisabledFromRegistration"
                @click="onRegisterClick" />
    </hh-form>
</template>

<script>

import Vuex from 'vuex'

import AuthManager from '@/auth/auth-manager'

import AnalyticsLogger from '@/components/analytics-logger'
import CopyPresenter from '@/components/app-core/modals/copy-presenter'
import Policies from '@/components/copy'

export default {

    props: {
        error: {
            type: String,
            default: null,
        },
    },

    mixins: [
        AnalyticsLogger,
    ],

    computed: {

        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {?PropertyAsJson}
             */
            property: stateStore => stateStore.property,
        }),

        evaluationOptions() {
            return [ {
                key: 'yes-eval',
                text: 'Yes please',
                value: true,
            }, {
                key: 'no-eval',
                text: 'No thanks',
                value: false,
            } ]
        },

        marketingOptions() {
            return [ {
                key: 'yes-mktg',
                text: 'Yes please',
                value: true,
            }, {
                key: 'no-mktg',
                text: 'No thanks',
                value: false,
            } ]
        },

        externalPolicy() {
            return this.$scheme.policyData.licensee.policies.privacy
        },

        isDisabledFromRegistration() {
            return false
        },

        schemeName() {
            return this.$scheme.title
        },

        /**
         * @returns {boolean}
         */
        showEvaluationConsent() {
            return this.$scheme.showEvaluationConsent
        },

        /**
         * @returns {boolean}
         */
        showMarketingConsent() {
            return this.$scheme.showMarketingConsent
        },
    },

    data() {
        return {

            /**
             * @returns {?string}
             */
            password: null,

            /**
             * @returns {?string}
             */
            confirmPassword: null,

            /**
             * @returns {?string}
             */
            email: null,

            /**
             * @returns {boolean}
             */
            termsAccepted: false,

            /**
             * @returns {boolean}
             */
            operationInProgress: false,

            /**
             * @returns {?string}
             */
            firstName: null,

            /**
             * @returns {?string}
             */
            lastName: null,

            /**
             * @returns {?string}
             */
            contactTelephoneNumber: null,

            /**
             * @returns {?boolean}
             */
            marketingConsentGiven: null,

            /**
             * @returns {?boolean}
             */
            evaluationConsentGiven: null,

            /**
             * @returns {boolean}
             */
            registering: false,

            /**
             * @returns {boolean}
             */
            showRecaptcha: false,

            /**
             * @returns {boolean}
             */
            reCaptchaVerified: false,
        }
    },

    methods: {

        _emitErrorState(msg) {
            const errState = msg && msg.length ? { error: true, msg } : { error: false, msg: '' }
            this.$emit('error-state-changed', errState)
        },

        _handleAutofilledEntries() {
            this.email = this.$refs['model.Email'].$el.childNodes[0].value
            this.firstName = this.$refs['model.FirstName'].$el.childNodes[0].value
            this.lastName = this.$refs['model.LastName'].$el.childNodes[0].value
            this.contactTelephoneNumber = this.$refs['model.ContactTelephoneNumber'].$el.childNodes[0].value
            this.password = this.$refs['model.Password'].$el.childNodes[0].value
            this.confirmPassword = this.$refs['model.ConfirmPassword'].$el.childNodes[0].value
            this.$logger.logObject({ email: this.email, firstName: this.firstName, lastName: this.lastName, tel: this.contactTelephoneNumber }, 'Autofill values captured', 'Registration Form')
        },

        _resetPasswords() {
            this.$logger.logObject(this.$refs, 'refs', 'Registration Form')
            this.password = null
            this.confirmPassword = null
            this._setInputElementValue(this.$refs['model.Password'].$el, '')
            this._setInputElementValue(this.$refs['model.ConfirmPassword'].$el, '')
        },

        /**
         * Sets the value of an input element.
         * @param {Element} el
         * @param {string|number} value
         * @private
         */
        _setInputElementValue(el, value) {
            el.childNodes[0].value = value
        },

        onPrivacyClick() {
            this.$eventbus.$emit(this.$appevents.showModal, CopyPresenter, {
                data: { title: 'Privacy Notice' },
                cssModifier: 'policy',
                nestedComponent: Policies.Privacy,
            })
        },

        onReCaptchaVerify(result) {
            this.reCaptchaVerified = result
        },

        async onRegisterClick() {
            try {
                this._handleAutofilledEntries()
                this.registering = true
                this.trackEvent('registration', 'registration_started')

                await AuthManager.registerAsync(this.email, this.password, this.confirmPassword, this.property,
                    this.firstName, this.lastName, this.contactTelephoneNumber, this.marketingConsentGiven, this.evaluationConsentGiven,
                    this.termsAccepted, this.showMarketingConsent, this.showEvaluationConsent)

                this.trackEvent('registration', 'registration_complete')

                this.$emit('registered', this.email)
                this.registering = false
            } catch (err) {
                this.registering = false
                if (err && err.response && err.response.status === 403) {
                    const reCaptchaModelState = { 'model.Recaptcha': [ 'Please confirm you are a human being' ] }

                    if (err.response.data)
                        err.response.data.modelState = reCaptchaModelState
                    else
                        err.response.data = { modelState: reCaptchaModelState }

                    err.message = 'You will need to fill out a reCAPTCHA again'
                    this.showRecaptcha = true
                }

                this._resetPasswords()
                const validation = this.$serverValidator.bindResults(err, this.$refs)
                this._emitErrorState(this.$serverValidator.formatErrorMessageFromResults(validation, 'User registration failed'))
            }
        },

        onConfirmPasswordChange(ev) {
            if (this.confirmPassword != null)
                this.resetValidation(ev)
        },

        onPasswordChange(ev) {
            if (this.password != null)
                this.resetValidation(ev)
        },

        onTermsClick() {
            this.$eventbus.$emit(this.$appevents.showModal, CopyPresenter, {
                data: { title: 'Terms & Conditions' },
                cssModifier: 'policy',
                nestedComponent: Policies.TermsAndConditions,
            })
        },

        resetValidation(extraRefs) {
            this.$serverValidator.clear(this.$refs)
            if (extraRefs) this.$serverValidator.clear(extraRefs)
            this._emitErrorState(null)
        },
    },
}

</script>

<style lang="scss">

@mixin registration-link {

    @include element('link') {
        font-weight: bold;

        &:hover {
            cursor: pointer;
        }
    }
}

.registration-form {
    @include registration-link;

    @include element('button') {
        margin-bottom: 0.5em !important;
        min-width: rem(160px);

        @include bp-min-small {
            margin-bottom: 0 !important;
        }

        @include modifier('ok') {
            float: right;

            @include bp-min-small {
                margin-top: 80px;
            }
        }
    }

    @include element('data-notes') {
        @include sub-notes
    }

    @include element('input') {
        width: 100%;
        @include form-group-split;

        @include modifier('consent') {
            font-size: 0.8rem;
            font-weight: bold;
            position: relative;
            top: -5px;

            li {
                display: inline-block;
            }
        }
    }

    @include element('terms') {
        font-size: 0.7rem;
        padding: 1rem 0;
        @include registration-link;

        @include element('box') {
            position: relative;
            top: rem(2px);
        }
    }

    @include element('validator') {
        @include form-group-split;
    }
}

</style>
